import { useEffect, useMemo, useRef } from "react";
import { api } from "../api";
import { CountryInput } from "../components/CountryInput";
import type { Rgb } from "../components/flag/flag";
import { StitchBoard } from "../components/flag/StitchBoard";
import "../components/flag/flag.css";
import { GiveUp } from "../components/GiveUp";
import { ResultSheet } from "../components/ResultSheet";
import { Centered, Header, Shell } from "../components/Shell";
import { Toast } from "../components/Toast";
import { useGame } from "../useGame";

interface Guess {
  code: string;
  name: string;
  pct: number;
}
interface View {
  w: number;
  h: number;
  palette: [number, number, number][];
  guesses: Guess[];
  revealed: string;
  hints: string[];
  tipsLeft: number;
  answer?: { code: string; name: string };
}

/** The last stitches, the border and the hoop's bounce get their moment before the sheet. */
const OPTS = { sheetDelayMs: 2400 };

const COPY = {
  unknown_country: "Не знаю такої країни",
  already_guessed: "Вже було",
  no_tips: "Підказки закінчились",
};

/** Paints a palette-digit grid; "-" cells are the unknown colour. */
function GridCanvas({ grid, w, h, palette, className }: { grid: string; w: number; h: number; palette: [number, number, number][]; className?: string }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    if (!ctx) return;
    const img = ctx.createImageData(w, h);
    for (let i = 0; i < w * h; i++) {
      const ch = grid[i] ?? "-";
      const [r, g, b] = ch === "-" ? [60, 60, 64] : (palette[Number(ch)] ?? [60, 60, 64]);
      img.data[i * 4] = r;
      img.data[i * 4 + 1] = g;
      img.data[i * 4 + 2] = b;
      img.data[i * 4 + 3] = 255;
    }
    ctx.putImageData(img, 0, 0);
  }, [grid, w, h, palette]);
  return <canvas ref={ref} width={w} height={h} className={className} style={{ imageRendering: "pixelated" }} />;
}

export function Flaggle({ date }: { date?: string | undefined }) {
  const g = useGame<View>("flaggle", COPY, date, OPTS);
  const state = g.screen.kind === "game" ? g.screen.state : null;
  const finished = state !== null && state.status !== "playing";
  const guessed = useMemo(() => new Set(state?.view.guesses.map((x) => x.name) ?? []), [state?.view.guesses]);
  const lastOverlap = (state?.extra as { overlap?: string } | undefined)?.overlap;

  if (g.screen.kind === "loading") return <Shell><Centered>…</Centered></Shell>;
  if (g.screen.kind === "error") {
    return (
      <Shell>
        <Centered>
          <div>{g.screen.message}</div>
          {g.screen.retry && (
            <button type="button" onClick={() => void g.load()} className="rounded-xl bg-tgbutton px-5 py-2.5 text-sm font-semibold text-tgbuttontext">
              ще раз
            </button>
          )}
        </Centered>
      </Shell>
    );
  }
  const s = g.screen.state;
  const v = s.view;

  return (
    <Shell header={<Header left={`#${s.number}`} title="FLAGGLE" right={`🔥 ${s.me.streak}`} />}>
      <CountryInput
        disabled={finished}
        busy={g.busy}
        exclude={guessed}
        onSubmit={(name) => void g.run(() => api.guess<View>("flaggle", name, date))}
      />
      <div className="px-6 py-2">
        <div className="mx-auto w-full max-w-[310px]">
          <StitchBoard w={v.w} h={v.h} palette={v.palette as Rgb[]} revealed={v.revealed} status={s.status} />
        </div>
        <div className="mt-1.5 text-center text-[11px] text-tghint">
          {finished ? (v.answer?.name ?? "") : "вишиті клітинки — справжні кольори прапора"}
        </div>
        {v.hints.length > 0 && (
          <div className="mt-2 space-y-1">
            {v.hints.map((h) => (
              <div key={h} className="fl-hint">
                💡 {h}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex-1 space-y-[6px] overflow-y-auto px-4 pb-2">
        {[...v.guesses].reverse().map((r, i) => (
          <div key={r.code} className="grid h-[44px] grid-cols-[auto_1fr_auto_auto] items-center gap-3 rounded-lg bg-tgsection px-3 text-[15px]">
            {r ? (
              <>
                <img src={`/flags/${r.code.toLowerCase()}.svg`} alt="" className="h-6 w-8 rounded-[3px] object-cover" />
                <span className="truncate font-semibold">{r.name}</span>
                {i === 0 && lastOverlap && !finished ? (
                  <GridCanvas grid={lastOverlap} w={v.w} h={v.h} palette={v.palette} className="h-6 w-9 rounded-[3px]" />
                ) : (
                  <span />
                )}
                <span className={`w-12 text-right font-bold tabular-nums ${r.pct === 100 ? "text-ok" : r.pct >= 60 ? "text-warm" : "text-tghint"}`}>
                  {r.pct}%
                </span>
              </>
            ) : (
              <span className="text-[rgba(128,128,128,0.5)]">·</span>
            )}
          </div>
        ))}
      </div>

      {!finished && (
        <div className="flex gap-2 px-4 pb-[calc(var(--tg-safe-area-inset-bottom,0px)+10px)] pt-1">
          <div className="flex flex-1 items-center text-xs text-tghint">спроб: {v.guesses.length}</div>
          <button
            type="button"
            disabled={g.busy || v.tipsLeft <= 0}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => void g.run(() => api.guess<View>("flaggle", "?", date))}
            className="flex-1 rounded-xl bg-[rgba(128,128,128,0.18)] py-3 text-sm font-semibold active:opacity-70 disabled:opacity-40"
          >
            💡 підказка{v.tipsLeft > 0 ? ` (${v.tipsLeft})` : ""}
          </button>
          <GiveUp busy={g.busy} onGiveUp={() => void g.run(() => api.giveUp<View>("flaggle", date))} />
        </div>
      )}

      <Toast message={g.toast} />
      {g.sheetOpen && finished && (
        <ResultSheet
          state={s}
          archive={Boolean(date)}
          title={s.status === "won" ? "Є" : "Здався"}
          subtitle={
            s.status === "won"
              ? `${v.guesses.length} спроб · стрік ${s.me.streak} 🔥`
              : `це був ${v.answer?.name ?? "?"} · стрік згорів`
          }
          scoreLabel="avg спроб"
          formatScore={(n) => `${n} спроб`}
          onDismiss={() => g.setSheetOpen(false)}
        >
          {v.answer && (
            <div className="mt-4 flex items-center justify-center gap-3">
              <img src={`/flags/${v.answer.code.toLowerCase()}.svg`} alt="" className="h-8 rounded-sm" />
              <span className="text-lg font-semibold">{v.answer.name}</span>
            </div>
          )}
        </ResultSheet>
      )}
    </Shell>
  );
}
